import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Alert, Input, Heading, Text, Button, GridRow, GridColumn, GridWrapper } from 'react-onei';
import { withRouter } from "react-router-dom";

import TagService from '../../../services/TagService';
import ApiService from '../../../services/ApiService';
import Spinner from '../../Spinner/Spinner';
import { EMAIL_TYPE, MOBILE_TYPE, MOBILE_ACCOUNT } from '../../../helpers/account';
import UserContext from '../../../context/UserContext';
import { events } from '../../../conf/tags_ga.conf';

export class ContactView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      contactMobile: '',
      contactMail: '',
      messageHelp: null,
      loading: true,
      requesting: false,
      errorMobile: undefined,
      errorMail: undefined,
      isSubmitted: false,
      hasDisplayedWarning: false,
    };
    this.mobileInputRef = React.createRef();
    this.emailInputRef = React.createRef();
  };

  async componentDidMount() {
    await this.getContactsData();
  }

  /**
   * Get all contacts data
   */
  getContactsData = async () => {
    const { identitiesId } = this.context;
    const { history } = this.props;

    try {
      const contacts = await ApiService.getIdentitiesContacts({ identitiesId });

      if (contacts.length > 0) {
        for (const contact of contacts) {
          const { contactData } = await this.getContactData(contact);

          if (contact.contactType === MOBILE_TYPE) {
            this.setState({ contactMobile: contactData });
          } else if (contact.contactType === EMAIL_TYPE) {
            this.setState({ contactMail: contactData });
          }
        }
      }
      this.setState({ loading: false });
    } catch (err) {
      history.push({
        pathname: '/error',
        state: {
          code: err.response?.data?.code,
        }
      });
    }
  };

  /**
   * Get full contact data
   * @param {Object} contact 
   */
  getContactData = async (contact) => {
    const { identitiesId } = this.context;

    try {
      const res = await ApiService.getContactWayData({
        identitiesId,
        hashId: contact.contactId,
      });
      return res.data.data;
    } catch (err) {
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { contactMobile, contactMail, errorMobile, errorMail } = this.state;
    const { isSubmitted } = this.state;

    // Set helper when contacts changes
    if (
      prevState.contactMobile !== contactMobile ||
      prevState.contactMail !== contactMail ||
      prevState.errorMail !== errorMail
    ) {
      this.setHelpMessage();
    }
    if (isSubmitted && prevProps.errorMail !== errorMail && errorMail && !this.isEmptyEmailContactMessageShown()) {
      // Focus mail input when receive mail error after submit
      this.emailInputRef.current.focus();
    }
    if (isSubmitted && prevProps.errorMobile !== errorMobile && errorMobile) {
      // Focus mobile input when receive mobile error after submit
      this.mobileInputRef.current.focus();
    }
  }

  /**
   * Set correct help message
   */
  setHelpMessage = () => {
    const { identities } = this.context;
    const { contactMobile, contactMail } = this.state;
    const { wording } = this.context;
    const { WLOST_513, WLOST_520, WLOST_521 } = wording;

    if (!contactMobile && !contactMail) {
      this.setState({ messageHelp: WLOST_520 });
    } else if (contactMail.toLowerCase() === identities.login.toLowerCase() && identities.accountLevel !== MOBILE_ACCOUNT 
              && this.isEmailOrangeDomain(identities.login.toLowerCase())) {
      this.setState({
        messageHelp: WLOST_513,
        hasDisplayedWarning: true,
      });
    } else if (!contactMobile || (!contactMail && !this.isEmptyEmailContactMessageShown())) 
    {
      this.setState({ messageHelp: WLOST_521 });
    } else {
      this.setState({ messageHelp: null });
    }
  }

  /**
   * Set value or error state on change
   */
  handleChange = (e, type) => {
    const { value } = e.target;

    this.setState((state) => {
      return {
        contactMail: type === EMAIL_TYPE ? value.trim() : state.contactMail,
        contactMobile: type === MOBILE_TYPE ? value.trim() : state.contactMobile,
        errorMail: type === EMAIL_TYPE ? undefined : state.errorMail,
        errorMobile: type === MOBILE_TYPE ? undefined : state.errorMobile,
        isSubmitted: false,
      };
    });
  };

  /**
   * Handle blur
   */
  handleBlur = (e, type) => {
    const { contactMobile, contactMail } = this.state;
    const contact = type === EMAIL_TYPE ? contactMail : contactMobile;

    if (e.relatedTarget && e.relatedTarget.type === "submit") {
      return
    } else {
      this.checkCredentials(contact, type);
    }
  };

  /**
   * Check credentials
   */
  checkCredentials = async (login, type) => {
    const { wording } = this.context;
    const { WLOST_505, WLOST_506 } = wording;
    const { history } = this.props;

    try {
      await ApiService.checkCredentials({ login });
    } catch (err) {
      if (err.response && err.response.data && err.response.data.code) {
        switch (err.response.data.code) {
          case 412004:
            this.setState((state) => {
              return {
                errorMobile: type === MOBILE_TYPE ? WLOST_505 : state.errorMobile,
                errorMail: type === EMAIL_TYPE ? WLOST_506 : state.errorMail,
              };
            });
            break;
          default:
            if (err.response.status === 403) {
              history.push({
                pathname: '/error',
                state: {
                  code: err.response.data.code,
                }
              });
            }
            break;
        }
      }
    }
  }

  /**
   * Handle submit form
   */
  handleSubmit = async (e) => {
    e.preventDefault();

    const { setStep, wording } = this.context;
    const { history } = this.props;
    const {
      contactMobile, contactMail, hasDisplayedWarning,
    } = this.state;
    const { WLOST_505, WLOST_506, WLOST_507, WLOST_512 } = wording;

    TagService.sendEvent(events.updateContacts);

    this.setState({ requesting: true, isSubmitted: true });

    try {
      const res = await ApiService.updateContacts({
        mobile: contactMobile,
        mail: contactMail,
        hasDisplayedWarning,
      });
      setStep(`/${res.data.step}`);
      history.push(`/${res.data.step}`);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.code) {
        switch (err.response.data.code) {
          case 412004:
            if (err.response.data.field === "mobile") {
              this.setState({ errorMobile: WLOST_505 });
            } else if (err.response.data.field === "mail") {
              this.setState({ errorMail: WLOST_506 });
            }
            break;
          case 412005:
            this.setState({ errorMail: WLOST_512 });
            break;
          case 412010:
            this.setState({ errorMail: WLOST_507 });
            TagService.sendEvent(events.errorMessageMailDelete);
            break;
          default:
            history.push({
              pathname: '/error',
              state: {
                code: err.response.data.code,
              }
            });
            break;
        }
      }
    }
    this.setState({ requesting: false });
  };

  /**
   * Get if one contact have error
   */
  isEmptyEmailContactMessageShown = () => {
    const { wording } = this.context;
    const { WLOST_507 } = wording;
    const { errorMail } = this.state;

    return errorMail === WLOST_507;
  };

  /**
   * check if ulo is part of Orange domain
   * @param {user login} email 
   */
  isEmailOrangeDomain = (email) => {
    const domain = email.split("@")[1];
    const orangeDomains = ["orange.fr", "wanadoo.fr"];
    return orangeDomains.indexOf(domain) > -1;
  };

  /**
   * Get if one contact have error
   */
  isContactError = () => {
    const { errorMail, errorMobile } = this.state;

    return errorMail !== undefined || errorMobile !== undefined;
  };

  render() {
    const {
      contactMobile, contactMail, errorMobile, errorMail, messageHelp, loading, requesting,
    } = this.state;
    const { wording } = this.context;
    const { WLOST_500, WLOST_501, WLOST_503, WLOST_504, WLOST_513, WBUTTON_001 } = wording;
    const description = <span dangerouslySetInnerHTML={{ __html: WLOST_501 }}></span>;

    if (loading) {
      return <Spinner />;
    }
    return (
      <GridWrapper>
        <form onSubmit={this.handleSubmit} method="post">

          <GridRow>
            <GridColumn>
              <Heading as="h3" id="add-contact-subtitle" className="spacing-m">{WLOST_500}</Heading>
              <Text id="add-contact-description">
              { description }
              </Text>
            </GridColumn>
          </GridRow>

          <GridRow>
            <GridColumn size={{ xs: 1, m: 4 / 12 }}>
              <Input
                ref={this.mobileInputRef}
                id="add-mobile"
                defaultValue={contactMobile}
                error={errorMobile}
                maxLength={15}
                onBlur={e => this.handleBlur(e, MOBILE_TYPE)}
                onChange={e => this.handleChange(e, MOBILE_TYPE)}
                label={WLOST_503}
                displayOptionalLabel={false}
              />
              <Input
                ref={this.emailInputRef}
                id="add-mail"
                defaultValue={contactMail}
                error={errorMail}
                maxLength={254}
                onBlur={e => this.handleBlur(e, EMAIL_TYPE)}
                onChange={e => this.handleChange(e, EMAIL_TYPE)}
                label={WLOST_504}
                displayOptionalLabel={false}
                className="spacing-s"
              />
              {messageHelp && (
                <div id="contacts-message">
                  <Alert
                    className="spacing-xs"
                    id="contacts-helper"
                    mini
                    variant={messageHelp === WLOST_513 ? 'alert' : 'info'}
                    title={messageHelp}
                    tabindex="0"
                  />
                </div>
              )}
            </GridColumn>
          </GridRow>

          <GridRow>
            <GridColumn>
              <Button type="submit" id="contacts-submit-button" disabled={requesting || this.isContactError()} className="spacing-s">
                {WBUTTON_001}
              </Button>
            </GridColumn>
          </GridRow>

        </form>
      </GridWrapper>
    );
  }
}

ContactView.defaultProps = {
};

ContactView.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

ContactView.contextType = UserContext;

export default withRouter(ContactView);
